import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Devices, SignOut } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatET } from "../lib/dateUtils";

const navItems = [{ to: "/admin/active-sessions", label: "Active Sessions", icon: <Devices size={18} weight="duotone" />, testId: "nav-admin-active-sessions" }];

const ROLE_LABEL = { admin: "Admin", manager: "Manager", affiliate: "Rep", client: "Client", backoffice: "Back Office", trust_client: "Trust Client" };

export default function AdminActiveSessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggingOutId, setLoggingOutId] = useState(null);

  const load = () => {
    api.get("/admin/active-sessions")
      .then(({ data }) => setSessions(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, []);

  const forceLogout = async (session) => {
    setLoggingOutId(session.session_id);
    try {
      await api.post(`/admin/active-sessions/${session.session_id}/force-logout`);
      toast.success(`${session.display_name} has been logged out`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoggingOutId(null);
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Active Sessions" moduleBackPath="/admin">
      <div data-testid="admin-active-sessions-page">
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="Every device currently logged into the portal across all roles, updated automatically every 15 seconds.">Active Sessions</h1>
          <p className="text-igc-muted text-sm">Every user currently logged into the portal — force-logout a stuck or unwanted session instantly, without waiting on a republish.</p>
        </div>

        {loading && <p className="text-igc-muted text-sm" data-testid="admin-active-sessions-loading">Loading active sessions...</p>}

        {!loading && sessions.length === 0 && (
          <p className="text-igc-muted text-sm bg-igc-surface border border-igc-gold/10 p-5" data-testid="admin-active-sessions-empty">No one is currently logged in.</p>
        )}

        {!loading && sessions.length > 0 && (
          <div className="bg-igc-surface border border-igc-gold/15" data-testid="admin-active-sessions-table">
            <div className="grid grid-cols-5 gap-3 px-4 py-3 border-b border-igc-gold/15 text-igc-muted text-[10px] uppercase tracking-widest">
              <span>Role</span>
              <span>Name</span>
              <span>Login Time</span>
              <span>Last Activity</span>
              <span className="text-right">Action</span>
            </div>
            {sessions.map((s) => (
              <div key={s.session_id} className="grid grid-cols-5 gap-3 px-4 py-3 border-b border-igc-gold/10 items-center text-sm" data-testid={`active-session-row-${s.session_id}`}>
                <span className="text-igc-goldText text-xs uppercase tracking-wide" data-testid={`active-session-role-${s.session_id}`}>{ROLE_LABEL[s.role] || s.role}</span>
                <span className="text-igc-cream" data-testid={`active-session-name-${s.session_id}`}>{s.display_name} {s.code ? <span className="text-igc-muted text-xs">({s.code})</span> : null}</span>
                <span className="text-igc-muted text-xs" data-testid={`active-session-login-time-${s.session_id}`}>{s.login_time ? formatET(s.login_time, "MMM d, h:mm a") : "—"}</span>
                <span className="text-igc-muted text-xs" data-testid={`active-session-last-activity-${s.session_id}`}>{s.last_activity_time ? formatET(s.last_activity_time, "MMM d, h:mm a") : "—"}</span>
                <div className="text-right">
                  <Button
                    data-testid={`active-session-force-logout-${s.session_id}`}
                    onClick={() => forceLogout(s)}
                    disabled={loggingOutId === s.session_id}
                    title="Immediately ends this person's session and kicks them back to the login screen. Use this if someone left themselves logged in on a shared device or their session appears stuck."
                    className="h-8 text-xs bg-igc-danger/10 hover:bg-igc-danger/20 text-igc-danger border border-igc-danger/30 flex items-center gap-1.5 ml-auto"
                  >
                    <SignOut size={13} weight="bold" /> {loggingOutId === s.session_id ? "Logging out…" : "Force Logout"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
